import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Form from 'react-bootstrap/Form';
import './AutoCompleteInput.css';

import { useAutoCompleteAPI } from '../../../APIs/common';
export default function AutoCompleteInput({
  value,
  onChange,
  onSelect,
  apiEndpoint,
  placeholder = 'Type to search…',
  minChars = 2,
  disabled = false,
  // Draw the list on the page body, pinned under the field, so a pop-up
  // around it cannot cut it off or cover it.
  menuPortal = false
}) {
  const [typed, setTyped] = useState(value || '');
  const [isTyping, setIsTyping] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  // Sync internal state with value prop when it changes (for controlled component behavior)
  useEffect(() => {
    if (value !== undefined && value !== typed) {
      setTyped(value || '');
      setIsTyping(false);
    }
  }, [value]);

  const { results, isLoading } = useAutoCompleteAPI(typed, isTyping, apiEndpoint);

  // clicking outside closes dropdown
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keeps a portalled list under the field while the page or pop-up scrolls.
  useLayoutEffect(() => {
    if (!menuPortal || !showDropdown) return undefined;
    const place = () => {
      const r = containerRef.current?.getBoundingClientRect();
      if (r) setMenuPos({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [menuPortal, showDropdown]);

  const dropdown = showDropdown && (
    <div
      ref={dropdownRef}
      className="auto-dropdown"
      style={
        menuPortal && menuPos
          ? { position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width, marginTop: 0, zIndex: 10060 }
          : undefined
      }
    >
      {isLoading && <div className="auto-item disabled">Searching...</div>}

      {!isLoading && results.length === 0 && typed.length >= minChars && <div className="auto-item disabled">No results</div>}

      {results.map((item) => (
        <div
          key={item.id || item.value}
          className="auto-item"
          onMouseDown={() => {
            setTyped(item.name || item.label);
            setIsTyping(false);
            setShowDropdown(false);
            onSelect?.(item);
          }}
        >
          {item.name || item.label}
        </div>
      ))}
    </div>
  );

  return (
    <div className="position-relative" ref={containerRef}>
      <Form.Control
        type="text"
        placeholder={placeholder}
        value={typed}
        disabled={disabled}
        className="auto-input"
        onChange={(e) => {
          const text = e.target.value;
          setTyped(text);
          setIsTyping(true);
          onChange?.(text);
          setShowDropdown(true);
        }}
        onFocus={() => {
          if (typed.length >= minChars) setShowDropdown(true);
        }}
      />

      {menuPortal ? dropdown && createPortal(dropdown, document.body) : dropdown}
    </div>
  );
}
